// @ts-nocheck
import React, { useState, useRef, useEffect } from 'react';

interface LibraryItem {
  _id: string;
  title: string;
  description?: string;
  sourceDomain?: string;
  sourceType: string;
  contentType: string;
  originalUrl: string;
  canonicalUrl?: string;
  thumbnailUrl?: string;
  author?: string;
  publisher?: string;
  publishedAt?: string;
  createdAt?: string;
  readState: 'unread' | 'reading' | 'read';
  savedState: 'saved' | 'archived' | 'deleted';
  isFavorite?: boolean;
  tags?: string[];
  note?: string;
}

interface ReaderContentListProps {
  items: LibraryItem[];
  selectedItem: LibraryItem | null;
  onSelectItem: (item: LibraryItem) => void;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  selectedFilter: string; // 'all' | 'article' | 'video' | 'podcast' | 'social'
  onFilterChange: (f: string) => void;
  sortOrder: string; // 'newest' | 'oldest'
  onSortChange: (s: string) => void;
  onMarkAllRead?: () => void;
  isLoading: boolean;
  viewTitle?: string;
  collections?: any[];
  onEditItem?: (item: LibraryItem) => void;
  onToggleRead?: (item: LibraryItem, targetState?: string) => void;
  onDismissItem?: (item: LibraryItem) => void;
  onDeleteItem?: (item: LibraryItem) => void;
  onDeleteBatch?: (items: LibraryItem[]) => void;
  onRefreshFeed?: () => Promise<void> | void;
}

function formatRelativeDate(dateStr?: string) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 2) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default function ReaderContentList({
  items = [],
  selectedItem,
  onSelectItem,
  searchQuery,
  onSearchChange,
  selectedFilter,
  onFilterChange,
  sortOrder,
  onSortChange,
  onMarkAllRead,
  isLoading,
  viewTitle = 'Items',
  collections = [],
  onEditItem,
  onToggleRead,
  onDismissItem,
  onDeleteItem,
  onDeleteBatch,
  onRefreshFeed,
}: ReaderContentListProps) {
  // Selection state (selected by square checkbox)
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Feed refresh spinning state
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Individual item 10s countdown timers
  const [pendingDeletes, setPendingDeletes] = useState<{ [id: string]: number }>({});
  const timersRef = useRef<{ [id: string]: { timeout: any; interval: any } }>({});

  // Header batch delete 10s countdown timer
  const [batchDeleteState, setBatchDeleteState] = useState<{ isDeleting: boolean; secondsLeft: number } | null>(null);
  const batchTimerRef = useRef<{ timeout: any; interval: any } | null>(null);

  // Trigger feed refresh to fetch latest articles
  const handleRefreshClick = async () => {
    if (isRefreshing || !onRefreshFeed) return;
    setIsRefreshing(true);
    try {
      await onRefreshFeed();
    } catch (err) {
      console.error('[ReaderContentList] Refresh error:', err);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Clean up selected IDs that no longer exist in items
  useEffect(() => {
    setSelectedIds((prev) => prev.filter((id) => items.some((i) => i._id === id)));
  }, [items]);

  // Clean up all individual and batch timers on unmount
  useEffect(() => {
    return () => {
      Object.values(timersRef.current).forEach(({ timeout, interval }) => {
        clearTimeout(timeout);
        clearInterval(interval);
      });
      if (batchTimerRef.current) {
        clearTimeout(batchTimerRef.current.timeout);
        clearInterval(batchTimerRef.current.interval);
      }
    };
  }, []);

  // Selection helpers
  const isAllSelected = items.length > 0 && items.every((i) => selectedIds.includes(i._id));
  const isSomeSelected = selectedIds.length > 0 && !isAllSelected;

  const handleToggleSelectAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isAllSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(items.map((i) => i._id));
    }
  };

  const handleToggleItemSelect = (e: React.MouseEvent, itemId: string) => {
    e.stopPropagation();
    setSelectedIds((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
    );
  };

  // Individual item delete: 10s timer with animated border to cancel action, deletes automatically on expiry
  const handleIndividualDeleteClick = (e: React.MouseEvent, item: LibraryItem) => {
    e.stopPropagation();
    const itemId = item._id;

    // If already in 10s countdown, clicking cancels the action
    if (pendingDeletes[itemId] !== undefined) {
      if (timersRef.current[itemId]) {
        clearTimeout(timersRef.current[itemId].timeout);
        clearInterval(timersRef.current[itemId].interval);
        delete timersRef.current[itemId];
      }
      setPendingDeletes((prev) => {
        const copy = { ...prev };
        delete copy[itemId];
        return copy;
      });
      return;
    }

    // Start 10s countdown
    setPendingDeletes((prev) => ({ ...prev, [itemId]: 10 }));

    const interval = setInterval(() => {
      setPendingDeletes((prev) => {
        const cur = prev[itemId];
        if (cur === undefined || cur <= 1) return prev;
        return { ...prev, [itemId]: cur - 1 };
      });
    }, 1000);

    const timeout = setTimeout(() => {
      if (timersRef.current[itemId]) {
        clearInterval(timersRef.current[itemId].interval);
        delete timersRef.current[itemId];
      }
      setPendingDeletes((prev) => {
        const copy = { ...prev };
        delete copy[itemId];
        return copy;
      });
      setSelectedIds((prev) => prev.filter((id) => id !== itemId));
      onDeleteItem?.(item);
    }, 10000);

    timersRef.current[itemId] = { timeout, interval };
  };

  // Header batch delete: 10s timer to cancel action, deletes automatically on expiry
  const handleBatchDeleteClick = () => {
    // If currently in countdown, clicking cancels the action
    if (batchDeleteState?.isDeleting) {
      if (batchTimerRef.current) {
        clearTimeout(batchTimerRef.current.timeout);
        clearInterval(batchTimerRef.current.interval);
        batchTimerRef.current = null;
      }
      setBatchDeleteState(null);
      return;
    }

    // Target items: selected items, or all visible items if none selected
    const targetItems = selectedIds.length > 0
      ? items.filter((i) => selectedIds.includes(i._id))
      : items;

    if (targetItems.length === 0) return;

    setBatchDeleteState({ isDeleting: true, secondsLeft: 10 });

    const interval = setInterval(() => {
      setBatchDeleteState((prev) => {
        if (!prev || prev.secondsLeft <= 1) return prev;
        return { ...prev, secondsLeft: prev.secondsLeft - 1 };
      });
    }, 1000);

    const timeout = setTimeout(() => {
      if (batchTimerRef.current) {
        clearInterval(batchTimerRef.current.interval);
        batchTimerRef.current = null;
      }
      setBatchDeleteState(null);
      setSelectedIds([]);

      if (onDeleteBatch) {
        onDeleteBatch(targetItems);
      } else if (onDeleteItem) {
        targetItems.forEach((item) => onDeleteItem(item));
      }
    }, 10000);

    batchTimerRef.current = { timeout, interval };
  };

  return (
    <section className="reader-content-list-col" aria-label="Reading Content List">
      <div className="reader-content-list-header">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {items.length > 0 && (
              <button
                type="button"
                className={`reader-select-all-box ${isAllSelected ? 'checked' : isSomeSelected ? 'indeterminate' : ''}`}
                onClick={handleToggleSelectAll}
                title={isAllSelected ? 'Deselect all' : 'Select all news'}
                aria-label={isAllSelected ? 'Deselect all news' : 'Select all news'}
              >
                {isAllSelected && <i className="fi fi-rr-check" />}
                {!isAllSelected && isSomeSelected && <span className="reader-box-indeterminate-dash" />}
              </button>
            )}
            <h2 style={{ fontSize: '0.98rem', fontWeight: 600, margin: 0, letterSpacing: '-0.01em' }}>
              {viewTitle}
            </h2>
            {selectedIds.length > 0 && (
              <span className="reader-selection-count-badge">
                {selectedIds.length}
              </span>
            )}
          </div>

          {/* Action buttons: Refresh, Delete (icon only), Mark all read */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {/* Refresh feed button BEFORE delete button */}
            {onRefreshFeed && (
              <button
                type="button"
                className={`reader-btn reader-icon-action-btn ${isRefreshing ? 'is-refreshing' : ''}`}
                style={{ fontSize: '0.75rem', padding: '2px 8px', minHeight: '26px' }}
                onClick={handleRefreshClick}
                disabled={isRefreshing}
                title={isRefreshing ? 'Fetching latest new articles...' : 'Fetch latest new articles from feed'}
                aria-label="Fetch latest new articles from feed"
              >
                <i
                  className={`fi fi-rr-refresh ${isRefreshing ? 'reader-spin' : ''}`}
                  style={{ fontSize: '0.75rem' }}
                />
              </button>
            )}

            {/* Delete button: icon only */}
            {items.length > 0 && (
              <button
                type="button"
                className={`reader-btn reader-batch-delete-btn reader-icon-action-btn ${batchDeleteState?.isDeleting ? 'is-cancelling' : ''} ${selectedIds.length > 0 ? 'has-selection' : ''}`}
                style={{ fontSize: '0.75rem', padding: '2px 8px', minHeight: '26px', position: 'relative' }}
                onClick={handleBatchDeleteClick}
                title={
                  batchDeleteState?.isDeleting
                    ? `Deleting in ${batchDeleteState.secondsLeft}s — Click to cancel`
                    : selectedIds.length > 0
                    ? `Delete ${selectedIds.length} selected news`
                    : 'Delete news (select items first or click to delete all)'
                }
                aria-label={
                  batchDeleteState?.isDeleting
                    ? `Cancel deleting news (${batchDeleteState.secondsLeft}s remaining)`
                    : 'Delete news'
                }
              >
                {batchDeleteState?.isDeleting && (
                  <svg className="reader-btn-timer-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
                    <rect className="reader-btn-timer-track" x="1" y="1" width="98" height="98" rx="4" />
                    <rect
                      className="reader-btn-timer-indicator"
                      x="1" y="1" width="98" height="98" rx="4"
                      pathLength="100"
                    />
                  </svg>
                )}
                <i
                  className={batchDeleteState?.isDeleting ? 'fi fi-rr-cross' : 'fi fi-rr-trash'}
                  style={{ fontSize: '0.75rem' }}
                />
                {batchDeleteState?.isDeleting && (
                  <span className="reader-btn-timer-text">{batchDeleteState.secondsLeft}s</span>
                )}
                {!batchDeleteState?.isDeleting && selectedIds.length > 0 && (
                  <span className="reader-btn-count-dot">{selectedIds.length}</span>
                )}
              </button>
            )}

            {onMarkAllRead && items.some(i => i.readState === 'unread') && (
              <button
                className="reader-btn"
                style={{ fontSize: '0.75rem', padding: '2px 8px', minHeight: '26px' }}
                onClick={onMarkAllRead}
                title="Mark visible items as read"
              >
                <i className="fi fi-rr-check" style={{ fontSize: '0.7rem' }} />
                <span>Mark all read</span>
              </button>
            )}
          </div>
        </div>

        {/* Search Input */}
        <div className="reader-search-input-wrap">
          <i className="fi fi-rr-search" />
          <input
            type="search"
            className="reader-search-input"
            placeholder="Search items, authors, notes... ( / )"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>

        {/* Filter Pills and Sort */}
        <div className="reader-filter-row">
          <div className="reader-filter-pills" role="tablist" aria-label="Filter content type">
            {['all', 'article', 'video', 'podcast', 'social'].map((f) => (
              <button
                key={f}
                role="tab"
                aria-selected={selectedFilter === f}
                className={`reader-filter-pill ${selectedFilter === f ? 'active' : ''}`}
                onClick={() => onFilterChange(f)}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>

          <div className="reader-sort-wrap">
            <button
              type="button"
              className="reader-sort-btn"
              onClick={() => onSortChange(sortOrder === 'newest' ? 'oldest' : 'newest')}
              title={`Sorted by ${sortOrder === 'newest' ? 'Newest first' : 'Oldest first'}. Click to toggle.`}
              aria-label={`Sort items: currently ${sortOrder === 'newest' ? 'Newest' : 'Oldest'}`}
            >
              <span className="reader-sort-btn-text">
                {sortOrder === 'newest' ? 'Newest' : 'Oldest'}
              </span>
              <i
                className={`fi ${
                  sortOrder === 'newest'
                    ? 'fi-rr-arrow-small-down'
                    : 'fi-rr-arrow-small-up'
                } reader-sort-btn-icon`}
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
      </div>

      {/* Items Scrollable List */}
      <div className="reader-items-scroll">
        {isLoading && items.length === 0 ? (
          <div className="reader-empty-state">
            <div style={{ fontSize: '0.9rem', color: 'var(--reader-text-subtle)' }}>
              Loading items…
            </div>
          </div>
        ) : items.length === 0 ? (
          <div className="reader-empty-state">
            <i className="fi fi-rr-inbox reader-empty-icon" />
            <div className="reader-empty-title">No items found</div>
            <div className="reader-empty-subtitle">
              {searchQuery
                ? `No results matching "${searchQuery}".`
                : 'Save links or subscribe to RSS feeds to build your digital library.'}
            </div>
          </div>
        ) : (
          <>
            {isLoading && (
              <div className="reader-subtle-loading-indicator" aria-label="Updating items...">
                <div className="reader-subtle-loading-bar" />
              </div>
            )}
            {items.map((item) => {
            const isSelected = selectedItem && selectedItem._id === item._id;
            const isUnread = item.readState === 'unread';
            const isChecked = selectedIds.includes(item._id);
            const isPendingDelete = pendingDeletes[item._id] !== undefined;
            const secondsLeft = pendingDeletes[item._id] ?? 10;

            return (
              <article
                key={item._id}
                className={`reader-item-card ${isSelected ? 'selected' : ''} ${!isUnread ? 'read' : ''} ${isChecked ? 'item-checked' : ''} ${isPendingDelete ? 'item-pending-delete' : ''}`}
                onClick={() => {
                  if (isPendingDelete) {
                    // Clicking card cancels delete
                    handleIndividualDeleteClick({ stopPropagation: () => {} } as any, item);
                    return;
                  }
                  onSelectItem(item);
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    if (isPendingDelete) {
                      handleIndividualDeleteClick({ stopPropagation: () => {} } as any, item);
                      return;
                    }
                    onSelectItem(item);
                  }
                }}
              >
                <div className="reader-item-meta-top">
                  {/* Square Box for selecting news one by one */}
                  <span
                    className={`reader-item-checkbox ${isChecked ? 'checked' : ''}`}
                    role="checkbox"
                    aria-checked={isChecked}
                    tabIndex={0}
                    onClick={(e) => handleToggleItemSelect(e, item._id)}
                    onKeyDown={(e) => {
                      if (e.key === ' ' || e.key === 'Enter') {
                        e.stopPropagation();
                        e.preventDefault();
                        handleToggleItemSelect(e as any, item._id);
                      }
                    }}
                    title={isChecked ? 'Deselect news' : 'Select news'}
                    aria-label={isChecked ? `Deselect "${item.title}"` : `Select "${item.title}"`}
                  >
                    {isChecked && <i className="fi fi-rr-check" />}
                  </span>

                  {isUnread && <span className="reader-unread-indicator" title="Unread" />}
                  <span className="reader-item-source">{item.sourceDomain || 'Web'}</span>
                  {item.sourceType && item.sourceType !== 'generic' && (
                    <span className="reader-item-type-badge">{item.sourceType}</span>
                  )}
                  {item.collectionIds && item.collectionIds.length > 0 && (
                    <span style={{ display: 'inline-flex', gap: '4px', alignItems: 'center' }}>
                      {item.collectionIds.slice(0, 2).map((cid) => {
                        const colId = typeof cid === 'object' && cid && cid._id ? cid._id : String(cid);
                        const col = collections.find((c) => c._id === colId);
                        if (!col) return null;
                        return (
                          <span key={col._id} className="reader-item-col-pill" title={`Collection: ${col.name}`}>
                            <span
                              className="reader-col-pill-dot"
                              style={{ backgroundColor: col.color || 'var(--reader-accent)' }}
                            />
                            {col.name}
                          </span>
                        );
                      })}
                    </span>
                  )}
                  <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>{formatRelativeDate(item.publishedAt || item.createdAt)}</span>
                    {onEditItem && (
                      <button
                        type="button"
                        className="reader-card-edit-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditItem(item);
                        }}
                        title="Edit link & move collections"
                        aria-label={`Edit ${item.title}`}
                      >
                        <i className="fi fi-rr-edit" />
                      </button>
                    )}
                  </span>
                </div>

                {/* Only Title is shown — description is omitted */}
                <div className="reader-item-main">
                  <div className="reader-item-text-wrap">
                    <h3 className="reader-item-title">{item.title}</h3>
                  </div>

                  {item.thumbnailUrl && (
                    <img
                      src={item.thumbnailUrl}
                      alt=""
                      className="reader-item-thumb"
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  )}
                </div>

                {/* Bottom Actions Row: Delete (with 10s timer border), Dismiss, Mark as read */}
                <div className="reader-card-actions-row">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {onDeleteItem && (
                      <button
                        type="button"
                        className={`reader-card-action-btn delete-btn ${isPendingDelete ? 'is-timer-active' : ''}`}
                        onClick={(e) => handleIndividualDeleteClick(e, item)}
                        title={
                          isPendingDelete
                            ? `Deleting in ${secondsLeft}s — Click to cancel`
                            : 'Delete item'
                        }
                        aria-label={
                          isPendingDelete
                            ? `Cancel deleting "${item.title}" (${secondsLeft}s remaining)`
                            : `Delete "${item.title}"`
                        }
                      >
                        {isPendingDelete && (
                          <svg className="reader-btn-timer-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
                            <rect className="reader-btn-timer-track" x="1" y="1" width="98" height="98" rx="4" />
                            <rect
                              className="reader-btn-timer-indicator"
                              x="1" y="1" width="98" height="98" rx="4"
                              pathLength="100"
                            />
                          </svg>
                        )}
                        <i
                          className={isPendingDelete ? 'fi fi-rr-cross-small' : 'fi fi-rr-trash'}
                          style={{ fontSize: isPendingDelete ? '0.8rem' : '0.72rem' }}
                        />
                        {isPendingDelete && (
                          <span className="reader-btn-timer-text">{secondsLeft}s</span>
                        )}
                      </button>
                    )}

                    <button
                      type="button"
                      className="reader-card-action-btn dismiss-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDismissItem?.(item);
                      }}
                      title="Dismiss from inbox"
                      aria-label={`Dismiss "${item.title}"`}
                    >
                      <i className="fi fi-rr-cross-small" style={{ fontSize: '0.78rem' }} />
                      <span>Dismiss</span>
                    </button>
                  </div>

                  <button
                    type="button"
                    className={`reader-card-action-btn ${!isUnread ? 'read-active' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleRead?.(item, isUnread ? 'read' : 'unread');
                    }}
                    title={isUnread ? 'Mark as read' : 'Mark as unread'}
                    aria-label={isUnread ? `Mark "${item.title}" as read` : `Mark "${item.title}" as unread`}
                  >
                    <i
                      className={isUnread ? 'fi fi-rr-check' : 'fi fi-rr-check-circle'}
                      style={{ fontSize: '0.72rem' }}
                    />
                    <span>{isUnread ? 'Mark as read' : 'Read'}</span>
                  </button>
                </div>
              </article>
            );
          })}
        </>
      )}
      </div>
    </section>
  );
}
